<template>
    <div class="journey-videos">
        <aside-content-header with-date-picker :vehicle="currentJourney.vehicle" :is-hardware-online="isHardwareOnline"></aside-content-header>
        <div class="journey-videos__content">
            <v-expansion-panel class="journey-videos" expand v-model="expanded">
                <v-expansion-panel-content>
                    <template v-slot:header>
                        <h2>Request Video</h2>
                    </template>
                    <div class="journey-videos__request-form">
                        <v-form
                            ref="form"
                            v-model="valid"
                            lazy-validation
                        >
                            <div class="content">
                                <div class="inputs-wrapper">
                                    <div class="journey-videos__subtitle">Request Video Date and Time</div>
                                    <date-picker
                                        v-model="form.datetime"
                                        :clearable="false"
                                        type="datetime"
                                    />
                                    <div class="journey-videos__subtitle">Channels</div>
                                    <v-select
                                        v-model="form.channels"
                                        :items="channels"
                                        multiple
                                        class="small-field"
                                        label="Choose channels"
                                        item-text="key"
                                        item-value="id"
                                        outline
                                        required
                                        single-line
                                    ></v-select>
                                    <div class="journey-videos__subtitle">Video Length</div>
                                    <v-select
                                        v-model="form.duration"
                                        :items="durations"
                                        class="small-field"
                                        item-text="text"
                                        item-value="value"
                                        outline
                                        single-line
                                    ></v-select>
                                </div>
                                <div class="controls-wrapper">
                                    <sp-switch
                                        v-model="form.overlay"
                                        :color="form.overlay ? 'red' : ''"
                                        :value="form.overlay"
                                        label="With overlay"
                                        className="switch--white small-field"
                                        class="small-field"
                                    ></sp-switch>
                                    <div class="request-button">
                                        <sp-btn @click.native="requestMedia" sp-class="no-padding-right" icon="settings" text="Request video"></sp-btn>
                                    </div>
                                </div>
                            </div>
                        </v-form>
                    </div>
                </v-expansion-panel-content>
            </v-expansion-panel>

                <div class="journey-videos__list" v-if="medias.length > 0">
                    <no-image-video-item
                        v-for="(media, index) in medias"
                        :key="media.id"
                        :video="media"
                        :on-event-click="showEvent"
                    ></no-image-video-item>
                </div>
            <plug v-else icon="video-solid" title="No medias have been found" :width="45" :height="55" color="#e2e2e2"></plug>
        </div>
    </div>
</template>

<script>

import AsideContentHeader from '@/views/Tracking/components/Aside/AsideContentHeader/AsideContentHeader'
import Journeys from '@/models/Journeys'
import NoImageVideoItem from '@/components/atoms/NoImageVideoItem'
import Plug from '@/components/atoms/Plug'
import { Datetime } from 'vue-datetime'

import DatePicker from 'vue2-datepicker'
import '@/assets/scss/datepicker.scss'

import SpSelect from '@/components/atoms/Select'
import SpSwitch from '@/components/atoms/Switch'
import SpBtn from '@/components/forms/SpBtn'

import moment from 'moment'
import toast from '@/plugins/izitoast'
import { DeviceApi, DeviceCommandApi, MediaRequestApi } from '@/api'
import { requestedVideoDurations } from '@/helpers/GlobalHelperVariables'


import { mapGetters, mapActions } from "vuex";

const deviceApi = new DeviceApi()
const deviceCommandApi = new DeviceCommandApi()

export default {
    name: "JourneyVideos",
    components: {
        AsideContentHeader,
        NoImageVideoItem,
        Datetime,
        DatePicker,
        SpSelect,
        SpSwitch,
        SpBtn,
        Plug
    },
    props: {
        currentJourney: {
            type: [Journeys, null],
            required: true
        },
        isHardwareOnline: {
            type: [Boolean],
            required: true
        }
    },
    async mounted () {
        await this.getDeviceInfo()
    },
    data() {
        return {
            form: {
                date: '',
                time: '',
                datetime: moment()._d,
                channels: '',
                overlay: true,
                duration: 10
            },
            valid: false,
            loading: false,
            expanded: [true],
            medias: this.currentJourney.medias,
            durations: requestedVideoDurations,
            channels: [],
        }
    },
    computed: {
        ...mapGetters({
            getChannels: 'dictionaryModule/getChannels',
            getDateInJourneyTab: 'trackingModule/getDateInJourneyTab',
        }),
    },
    watch: {
        'currentJourney.medias': {
            handler(newMedia) {
                this.medias = newMedia
            }
        }
    },
    methods: {
        ...mapActions({
            updateCurrentJourneyMedia: 'trackingModule/updateCurrentJourneyMedia',
        }),
        async getDeviceInfo () {
            const response = await deviceApi.getDevice(this.currentJourney.vehicle.mediaDeviceId)

            const channelsFlag = (response.payload.channelFlag >>> 0).toString(2).split('').reverse()

            this.channels = this.getChannels.filter((channel) => {
                return 0 !== channel.id
            }).filter((channel, index) => {
                if (0 === channelsFlag[index] || null == channelsFlag[index]) {
                    return
                }

                return channel.id === Math.pow(2, index)
            })
            this.form.channels = this.channels.map(channel => channel.id);
        },
        async requestMedia () {
            this.loading = true
            this.valid = this.$refs.form.validate()
            if (!this.valid) {
                return
            }
            const date = this.form.datetime

            const response = await deviceCommandApi.requestVideo({
                hardwareId: this.currentJourney.vehicle.mediaHardwareId,
                date: date,
                channels: 0 === this.form.channels.length ? 0 : this.form.channels.reduce((a, b) => a + b, 0),
                videoLengthInSeconds: this.form.duration,
                withOverlay: this.form.overlay,
            })

            if (response.isSuccess) {
                toast.success('Media request sent')
                const formatedRequestedDate = moment(date).format('YYYY-MM-DD')
                if (this.getDateInJourneyTab === formatedRequestedDate) {
                    const mediasForCurrentDay = await this.getCurrentMedias()

                    if (mediasForCurrentDay.length) {
                        this.updateCurrentJourneyMedia(mediasForCurrentDay)
                    }
                }
            }
        },
        async getCurrentMedias () {
            const mediaRequestApi = new MediaRequestApi()
            const mediaResponse = await mediaRequestApi.getMediaRequests({
                videoTimeFrom: moment(this.form.datetime),
                videoTimeTo: moment(this.form.datetime),
                hardwareId: this.currentJourney.vehicle.mediaHardwareId
            })

            if (mediaResponse.isSuccess) {
                return mediaResponse.payload.items.filter(x => moment(moment(x.dateRequested).format('YYYY-MM-DD')).isSame(this.getDateInJourneyTab))
            }
            return null
        },
        showEvent (payload) {
            this.$store.commit('setEventId', payload)
            this.closeModals()
        }
    }
}
</script>

<style scoped lang="scss">
.journey-videos {
    &__content {
        padding: 10px 15px;

        ul {
            box-shadow: none;
            border-bottom: 1px solid #c1c1c1;
        }
    }

    &__list {
        margin-top: 10px;
        div {
            margin-bottom: 12px;
        }
    }

    &__title {
        font-weight: 700;
    }

    &__subtitle {
        font-size: 12px;
        font-weight: 700;
        padding: 10px 0 5px;
    }

    ::v-deep .mx-datepicker {
        input {
            height: 38px;
        }
    }

    &__request-form {
        .controls-wrapper {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-end;
            margin-bottom: 10px;
        }
        ::v-deep .datetime-picker {
            border: 1px solid rgba(0,0,0,0.54);
            border-radius: 8px;
            height: 37px;
            font-size: 14px;
            margin-bottom: 8px;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        .datetime-picker .vdatetime-popup__header,
        .datetime-picker .vdatetime-calendar__month__day--selected > span > span,
        .datetime-picker .vdatetime-calendar__month__day--selected:hover > span > span {
            background: $secondary-default;
        }
        .datetime-picker .vdatetime-year-picker__item--selected,
        .datetime-picker .vdatetime-time-picker__item--selected,
        .datetime-picker .vdatetime-popup__actions__button {
            color: $primary;
        }
        ::v-deep .datetime-picker > div {
            height: 10px;
            position: relative;
            width: 100%;
            .vdatetime-popup {
                position: absolute;
                top:15px;
                left:0;
                width: 340px;
                max-width: 340px;
                transform: translateX(-50%)
            }
        }
        ::v-deep .vdatetime-popup__header {
            background: $secondary-default !important;
        }
        ::v-deep .vdatetime-calendar__month__day--selected > span > span, .vdatetime-calendar__month__day--selected:hover > span > span {
            background: $secondary-default !important;
        }

        ::v-deep .switch__element {
            margin-bottom: 5px;
            .v-label {
                position: static !important;
            }
        }
    }
}
</style>
